import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowUp, ArrowDown, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { localizeApiDetail } from '../i18n/apiError'
import { publicAPI } from '../services/api'
import type {
  OrgDetailsAggregatedRow,
  OrgDetailsAggregatedResponse,
  OrgRole,
} from '../services/api'
import { exportToCsv, type ColumnDefinition } from '../utils/exportToCsv'
import { exportToPdf } from '../utils/exportToPdf'
import LoadingSpinner from './LoadingSpinner'
import ErrorMessage from './ErrorMessage'

interface Props {
  orgnr: string
  from: string
  to: string
  availableRoles: OrgRole[]
  varebil?: boolean
  classification?: string
  minePostnummer?: string
}

type SortKey = 'antall' | 'reg_date' | 'brand' | 'master_model'
type Order = 'asc' | 'desc'

const PAGE_SIZE = 50

// Translation keys, resolved with t() at render so a language switch applies.
const ROLE_LABELS = {
  kjopt: 'orgRoles.kjopt',
  leaset_inn: 'orgRoles.leasetInn',
  leaset_ut_naering: 'orgRoles.leasetUtNaering',
  leaset_ut_privat: 'orgRoles.leasetUtPrivat',
  leaset_ut_udefinert: 'orgRoles.leasetUtUdefinert',
} as const satisfies Record<OrgRole, string>

const ROLE_BADGE: Record<OrgRole, string> = {
  kjopt: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  leaset_inn: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
  leaset_ut_naering: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  leaset_ut_privat: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  leaset_ut_udefinert: 'bg-slate-100 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300',
}

const nbNoDate = (iso: string) => {
  try { return new Date(iso).toLocaleDateString('nb-NO') } catch { return iso }
}

export default function OrgDetailsAggregatedCarsTable({
  orgnr,
  from,
  to,
  availableRoles,
  varebil,
  classification,
  minePostnummer,
}: Props) {
  const { t } = useTranslation('components')
  const [searchParams, setSearchParams] = useSearchParams()

  const urlRole = searchParams.get('role')
  const role: OrgRole | 'alle' =
    urlRole && (availableRoles as string[]).includes(urlRole)
      ? (urlRole as OrgRole)
      : 'alle'
  const page = parseInt(searchParams.get('page') ?? '1', 10) || 1
  const sort = (searchParams.get('sort') as SortKey | null) ?? 'antall'
  const order = (searchParams.get('order') as Order | null) ?? 'desc'

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams)
      for (const [k, v] of Object.entries(updates)) {
        if (v === null) next.delete(k)
        else next.set(k, v)
      }
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const [data, setData] = useState<OrgDetailsAggregatedRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    publicAPI
      .getOrgDetailsAggregatedCars(orgnr, {
        from,
        to,
        ...(role !== 'alle' ? { role } : {}),
        ...(varebil !== undefined ? { varebil } : {}),
        ...(classification ? { classification } : {}),
        ...(minePostnummer ? { mine_postnummer: minePostnummer } : {}),
        page,
        page_size: PAGE_SIZE,
        sort,
        order,
      })
      .then((res) => {
        if (cancelled) return
        const body = res.data as OrgDetailsAggregatedResponse
        setData(body.data)
        setTotal(body.pagination.total)
      })
      .catch((err) => {
        if (cancelled) return
        setError(localizeApiDetail(err?.response?.data?.detail) || i18n.t('components:orgDetailsCars.loadError'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [orgnr, from, to, role, page, sort, order, varebil, classification, minePostnummer])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handleSort = (key: SortKey) => {
    if (sort === key) {
      updateParams({ order: order === 'asc' ? 'desc' : 'asc', page: '1' })
    } else {
      updateParams({ sort: key, order: key === 'antall' ? 'desc' : 'asc', page: '1' })
    }
  }

  const sortIcon = (key: SortKey) => {
    if (sort !== key) return <ChevronsUpDown className="h-4 w-4 text-gray-400 inline" />
    return order === 'asc' ? (
      <ArrowUp className="h-4 w-4 inline" />
    ) : (
      <ArrowDown className="h-4 w-4 inline" />
    )
  }

  const columns: ColumnDefinition[] = [
    { key: 'reg_date', label: t('orgDetailsCars.regDate'), format: (v) => (v ? nbNoDate(String(v)) : '—') },
    { key: 'brand', label: t('orgDetailsCars.brand') },
    { key: 'master_model', label: t('orgDetailsCars.model'), format: (v) => (v ? String(v) : '—') },
    { key: 'variant', label: t('orgDetailsCars.variant'), format: (v) => (v ? String(v) : '—') },
    { key: 'type', label: t('orgDetailsCars.type'), format: (v) => (ROLE_LABELS[v as OrgRole] ? t(ROLE_LABELS[v as OrgRole]) : String(v)) },
    { key: 'antall', label: t('orgDetailsCars.count') },
  ]

  const fetchAllRows = async (): Promise<OrgDetailsAggregatedRow[]> => {
    const chunk = 200
    const baseParams = {
      from,
      to,
      ...(role !== 'alle' ? { role } : {}),
      ...(varebil !== undefined ? { varebil } : {}),
      ...(classification ? { classification } : {}),
      ...(minePostnummer ? { mine_postnummer: minePostnummer } : {}),
      sort,
      order,
    }
    const first = await publicAPI.getOrgDetailsAggregatedCars(orgnr, {
      ...baseParams,
      page: 1,
      page_size: chunk,
    })
    const firstBody = first.data as OrgDetailsAggregatedResponse
    const all: OrgDetailsAggregatedRow[] = [...firstBody.data]
    const grandTotal = firstBody.pagination.total
    const pages = Math.ceil(grandTotal / chunk)
    for (let p = 2; p <= pages; p++) {
      const res = await publicAPI.getOrgDetailsAggregatedCars(orgnr, {
        ...baseParams,
        page: p,
        page_size: chunk,
      })
      all.push(...(res.data as OrgDetailsAggregatedResponse).data)
    }
    return all
  }

  const handleExport = async (fmt: 'csv' | 'pdf') => {
    if (exporting) return
    setExporting(true)
    try {
      const rows = await fetchAllRows()
      const metadata = {
        title: t('exportTitles.orgAggregated', { orgnr }),
        dateRange: `${from} – ${to}`,
        filters: [
          t('orgDetailsCars.roleFilter', { role: role === 'alle' ? t('orgDetailsCars.all') : t(ROLE_LABELS[role]) }),
          t('orgDetailsCars.sortFilter', { sort, order }),
        ],
      }
      if (fmt === 'csv') exportToCsv(rows, columns, metadata)
      else exportToPdf(rows, columns, metadata)
    } catch (err) {
      console.error('Export failed', err)
    } finally {
      setExporting(false)
    }
  }

  const pills: { value: OrgRole | 'alle'; label: string }[] = [
    { value: 'alle', label: t('orgDetailsCars.all') },
    ...availableRoles.map((r) => ({ value: r, label: t(ROLE_LABELS[r]) })),
  ]

  return (
    <div className="card overflow-x-auto relative">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-gray-200 dark:border-dark-border">
        <div className="flex flex-wrap gap-2">
          {pills.map((p) => (
            <button
              key={p.value}
              onClick={() => updateParams({
                role: p.value === 'alle' ? null : p.value,
                page: '1',
              })}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                role === p.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting || total === 0}
            className="btn-secondary text-sm disabled:opacity-50"
          >
            {exporting ? t('orgDetailsCars.exporting') : 'CSV'}
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting || total === 0}
            className="btn-secondary text-sm disabled:opacity-50"
          >
            {exporting ? t('orgDetailsCars.exporting') : 'PDF'}
          </button>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      {loading ? (
        <div className="p-8"><LoadingSpinner /></div>
      ) : data.length === 0 ? (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          {t('orgDetailsCars.noCars')}
        </div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-dark-border">
              <tr className="text-left text-gray-700 dark:text-gray-300">
                <th className="px-4 py-2 cursor-pointer select-none" onClick={() => handleSort('reg_date')}>
                  {t('orgDetailsCars.regDate')} {sortIcon('reg_date')}
                </th>
                <th className="px-4 py-2 cursor-pointer select-none" onClick={() => handleSort('brand')}>
                  {t('orgDetailsCars.brand')} {sortIcon('brand')}
                </th>
                <th className="px-4 py-2 cursor-pointer select-none" onClick={() => handleSort('master_model')}>
                  {t('orgDetailsCars.model')} {sortIcon('master_model')}
                </th>
                <th className="px-4 py-2">{t('orgDetailsCars.variant')}</th>
                <th className="px-4 py-2">{t('orgDetailsCars.type')}</th>
                <th className="px-4 py-2 text-right cursor-pointer select-none" onClick={() => handleSort('antall')}>
                  {t('orgDetailsCars.count')} {sortIcon('antall')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx} className="border-t border-gray-100 dark:border-dark-border hover:bg-gray-100 dark:hover:bg-dark-hover">
                  <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{nbNoDate(row.reg_date)}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.brand}</td>
                  <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.master_model ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{row.variant ?? '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE[row.type]}`}>
                      {t(ROLE_LABELS[row.type])}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">
                    {row.antall.toLocaleString('nb-NO')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-t border-gray-200 dark:border-dark-border">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {t('orgDetailsCars.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total: total.toLocaleString('nb-NO') })}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => updateParams({ page: '1' })} disabled={page === 1 || loading}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800">«</button>
              <button onClick={() => updateParams({ page: String(page - 1) })} disabled={page === 1 || loading}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800">{t('orgDetailsCars.previous')}</button>
              <span className="text-sm text-gray-700 dark:text-gray-300 px-2">{t('orgDetailsCars.pageOf', { page, total: totalPages })}</span>
              <button onClick={() => updateParams({ page: String(page + 1) })} disabled={page === totalPages || loading}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800">{t('orgDetailsCars.next')}</button>
              <button onClick={() => updateParams({ page: String(totalPages) })} disabled={page === totalPages || loading}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800">»</button>
            </div>
          </div>
        </>
      )}

      {exporting && (
        <div className="absolute top-2 right-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <Loader2 className="h-3 w-3 animate-spin" /> {t('orgDetailsCars.fetchingAll')}
        </div>
      )}
    </div>
  )
}
